import { useNavigate, Link } from "react-router-dom";
import { Play, Plus, Check, Info, Gift, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useTranslation } from "react-i18next";
import { CountdownPill } from "@/components/Countdown";
import { toast } from "sonner";

const HERO_BG = "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export const HeroBanner = ({ item }) => {
  const nav = useNavigate();
  const { user, subscribed, isFavorite, toggleFavorite } = useAuth();
  const { fromDisplay, freeAccess } = useCurrency();
  const { t } = useTranslation();
  if (!item) return null;
  const fav = isFavorite(item.id);
  const bg = item.backdrop || item.poster || HERO_BG;

  const onFav = async () => {
    if (!user) { toast.error("Sign in to save"); nav("/login"); return; }
    const added = await toggleFavorite(item.id);
    toast.success(added ? "Added to My List" : "Removed from My List");
  };

  return (
    <div data-testid="hero-banner" className="relative min-h-[72vh] sm:min-h-[85vh] w-full overflow-hidden">
      <div className="absolute inset-0">
        <img src={bg} alt={item.title} onError={(e) => { e.currentTarget.src = HERO_BG; }}
          className="w-full h-full object-cover scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C]/95 via-[#0A0A0C]/40 to-transparent" />
      </div>

      <div className="relative z-10 h-full flex flex-col justify-end min-h-[72vh] sm:min-h-[85vh] px-4 sm:px-8 lg:px-12 pb-16 max-w-3xl">
        <div className="animate-fade-up">
          {freeAccess && user === false && (
            <div className="mb-5">
              <Link to="/register" data-testid="hero-offer-spotlight"
                className="inline-flex items-center gap-2.5 pl-2 pr-4 py-2 rounded-full bg-gradient-to-r from-brand to-red-600 text-white font-extrabold text-sm sm:text-base shadow-xl shadow-brand/40 ring-2 ring-white/25 hover:scale-[1.04] transition-transform">
                <span className="grid place-items-center w-7 h-7 rounded-full bg-white/25"><Gift size={15} /></span>
                {t("free.badge")}
                <span className="font-semibold text-white/90">— {t("free.spotlight")}</span>
              </Link>
            </div>
          )}
          {!freeAccess && !subscribed && (
            <div className="mb-5">
              <Link to="/register" data-testid="hero-offer-spotlight"
                className="group inline-flex items-center gap-2.5 pl-2 pr-4 py-2 rounded-full bg-gradient-to-r from-brand to-red-600 text-white font-extrabold text-sm sm:text-base shadow-xl shadow-brand/40 ring-2 ring-white/25 hover:scale-[1.04] transition-transform">
                <span className="grid place-items-center w-7 h-7 rounded-full bg-white/25"><Gift size={15} /></span>
                <span className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-white" /></span>
                  24 HOURS FREE
                </span>
                <span className="font-semibold text-white/90">— then {fromDisplay || "R19.99"}/mo<span className="hidden sm:inline"> · Sign up free</span></span>
              </Link>
              <p data-testid="hero-countdown" className="mt-2 ml-1 flex items-center gap-1.5 text-xs sm:text-sm text-zinc-300">
                <Clock size={13} className="text-brand" />
                {t("promo.countdown")} <CountdownPill className="font-bold text-white" />
              </p>
            </div>
          )}
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-gold text-black uppercase tracking-wider">Featured</span>
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-white/15 backdrop-blur uppercase tracking-wider">
              {item.type === "live" ? "Live TV" : "Game"}
            </span>
            {item.year && <span className="text-zinc-300 text-sm">{item.year}</span>}
          </div>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none text-glow mb-4">
            {item.title}
          </h1>
          <p className="text-zinc-300 text-sm sm:text-base leading-relaxed line-clamp-3 mb-6 max-w-xl">
            {item.description}
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
            <button data-testid="hero-play-btn" onClick={() => nav(`/watch/${item.id}`)}
              className="flex items-center justify-center gap-2 bg-white text-black font-bold px-7 py-3 rounded-lg hover:bg-brand hover:text-white transition-colors w-full sm:w-auto">
              <Play size={20} fill="currentColor" /> Watch Now
            </button>
            {!user && (
              <button data-testid="hero-start-free-btn" onClick={() => nav("/register")}
                className="flex items-center justify-center gap-2 bg-brand text-white font-bold px-7 py-3 rounded-lg hover:bg-red-600 transition-colors shadow-lg shadow-brand/40 w-full sm:w-auto">
                <Gift size={20} /> {freeAccess ? t("free.cta") : t("promo.startFree")}
              </button>
            )}
            <div className="flex gap-3 sm:contents">
              <button data-testid="hero-fav-btn" onClick={onFav}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white/15 backdrop-blur border border-white/20 font-semibold px-6 py-3 rounded-lg hover:bg-white/25 transition-colors">
                {fav ? <Check size={20} /> : <Plus size={20} />} My List
              </button>
              <button data-testid="hero-info-btn" onClick={() => nav(`/watch/${item.id}`)}
                className="flex items-center justify-center gap-2 text-zinc-200 hover:text-white px-3 py-3 transition-colors">
                <Info size={20} /> Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
